import DOMPurify from "dompurify";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { directWebMediaUrl, isPrivateAccountMedia, resolveAccountMediaUrl } from "../runtime/accountMedia";

const richTag = /<[a-zA-Z][^>]*>/;
const allowedStyleProperties = new Set(["color", "background-color", "font-weight", "font-style", "text-decoration", "font-size", "text-align"]);

function encodedPlainStory(source: string): string {
  const node = document.createElement("div");
  node.textContent = source;
  return node.innerHTML
    .replace(/\n/g, "<br>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

function limitedStyle(value: string): string {
  return value.split(";").map((declaration) => declaration.trim()).filter(Boolean).map((declaration) => {
    const separator = declaration.indexOf(":");
    if (separator < 1) return "";
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const content = declaration.slice(separator + 1).trim();
    if (!allowedStyleProperties.has(property) || /url\s*\(|@import|expression\s*\(/i.test(content)) return "";
    return `${property}:${content}`;
  }).filter(Boolean).join(";");
}

function wrapDialogueQuotes(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) {
    const text = walker.currentNode as Text;
    const parent = text.parentElement;
    if (text.data.trim() && parent && !parent.closest("code,pre,.if-dialogue")) nodes.push(text);
  }
  const pattern = /("[^"]+"|“[^”]+”)/g;
  nodes.forEach((text) => {
    const matches = [...text.data.matchAll(pattern)];
    if (!matches.length) return;
    const fragment = document.createDocumentFragment();
    let offset = 0;
    matches.forEach((match) => {
      fragment.append(text.data.slice(offset, match.index));
      const span = document.createElement("span");
      span.className = "if-dialogue";
      span.textContent = match[0];
      fragment.append(span);
      offset = (match.index ?? 0) + match[0].length;
    });
    fragment.append(text.data.slice(offset));
    text.replaceWith(fragment);
  });
}

function buildStoryHtml(source: string, resolvedMedia: ReadonlyMap<string, string>): { html: string; privateMedia: string[] } {
  const raw = richTag.test(source) ? source : encodedPlainStory(source);
  const sanitized = DOMPurify.sanitize(raw, {
    ADD_TAGS: ["plot"],
    ADD_ATTR: ["style", "class", "colspan", "rowspan"],
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "button", "textarea", "select", "svg", "math"],
    FORBID_ATTR: ["srcset", "formaction"],
    ALLOW_DATA_ATTR: false,
    // Account media uses an opaque private reference until this component
    // exchanges it for a short-lived signed URL below. Keep only that one
    // custom scheme through sanitization; file/content/javascript stay blocked.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|blob):|data:image\/|supabase-private:\/\/)/i,
  });
  const parsed = new DOMParser().parseFromString(`<main>${sanitized}</main>`, "text/html");
  const root = parsed.body.firstElementChild as HTMLElement;
  const privateMedia = new Set<string>();

  root.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const style = limitedStyle(element.getAttribute("style") ?? "");
    if (style) element.setAttribute("style", style); else element.removeAttribute("style");
    element.removeAttribute("class");
  });
  root.querySelectorAll<HTMLAnchorElement>("a").forEach((anchor) => {
    if (!/^https?:\/\//i.test(anchor.href)) anchor.removeAttribute("href");
    else { anchor.target = "_blank"; anchor.rel = "noreferrer noopener"; }
  });
  root.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
    const sourceValue = image.getAttribute("src")?.trim() ?? "";
    const resolved = resolvedMedia.get(sourceValue) ?? directWebMediaUrl(sourceValue);
    if (resolved) image.src = resolved;
    else {
      image.removeAttribute("src");
      image.className = "legacy-media-unavailable";
      image.alt = image.alt || "ยังไม่พบไฟล์ภาพนี้";
      if (isPrivateAccountMedia(sourceValue)) privateMedia.add(sourceValue);
    }
    image.loading = "lazy";
  });
  wrapDialogueQuotes(root);
  return { html: root.innerHTML, privateMedia: [...privateMedia] };
}

function LegacyHtml({ source, className = "" }: { source: string; className?: string }) {
  const initial = useMemo(() => buildStoryHtml(source, new Map()), [source]);
  const [html, setHtml] = useState(initial.html);
  useEffect(() => {
    let active = true;
    setHtml(initial.html);
    if (initial.privateMedia.length) {
      void Promise.all(initial.privateMedia.map(async (item) => [item, await resolveAccountMediaUrl(item)] as const)).then((pairs) => {
        if (active) setHtml(buildStoryHtml(source, new Map(pairs)).html);
      });
    }
    return () => { active = false; };
  }, [initial, source]);
  return <div className={`legacy-rich-text ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

function nativeInline(source: string, keyPrefix: string): ReactNode[] {
  const pattern = /(\*\*[^*\n]+\*\*|(?<!\*)\*[^*\n]+\*(?!\*)|`[^`\n]+`|"[^"\n]+"|“[^”\n]+”)/g;
  const nodes: ReactNode[] = [];
  let offset = 0;
  for (const match of source.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > offset) nodes.push(source.slice(offset, index));
    const token = match[0];
    const key = `${keyPrefix}:${index}`;
    if (token.startsWith("**")) nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith("*")) nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    else if (token.startsWith("`")) nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    else nodes.push(<span className="if-dialogue" key={key}>{token}</span>);
    offset = index + token.length;
  }
  if (offset < source.length) nodes.push(source.slice(offset));
  return nodes;
}

/** Native path for new narration; only imported legacy HTML uses DOMPurify. */
export function StoryRichText({ source, className = "" }: { source: string; className?: string }) {
  if (richTag.test(source)) return <LegacyHtml source={source} className={className} />;
  const paragraphs = source.replace(/\r\n?/g, "\n").split(/\n{2,}/);
  return <div className={`native-story-text ${className}`.trim()}>{paragraphs.map((paragraph, paragraphIndex) => (
    <p key={`p:${paragraphIndex}`}>{paragraph.split("\n").map((line, lineIndex) => (
      <Fragment key={`l:${paragraphIndex}:${lineIndex}`}>{lineIndex > 0 && <br />}{nativeInline(line, `${paragraphIndex}:${lineIndex}`)}</Fragment>
    ))}</p>
  ))}</div>;
}

/** Compatibility name used by existing screens; plain text takes the native path. */
export function LegacyRichText(props: { source: string; className?: string }) {
  return <StoryRichText {...props} />;
}
